const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const buildUrl = (path) => `${API_BASE_URL}${path}`;

const requiredParam = (value, name) => {
    const normalized = String(value ?? '').trim();
    if (!normalized) {
        throw new Error(`${name} is required`);
    }
    return normalized;
};

export const buildStockPriceHistoryPath = ({ ticker, startDate, endDate, range } = {}) => {
    const params = new URLSearchParams({
        ticker: requiredParam(ticker, 'ticker').toUpperCase(),
        start_date: requiredParam(startDate, 'startDate'),
        end_date: requiredParam(endDate, 'endDate'),
    });
    if (range) {
        params.set('range', range);
    }

    return `/api/stock-price-history?${params.toString()}`;
};

const parseResponse = async (response) => {
    const text = await response.text();
    const data = text ? (() => {
        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    })() : null;

    if (!response.ok) {
        const message =
            (data && (data.error || data.message)) ||
            `Request failed with status ${response.status}`;
        const error = new Error(message);
        error.status = response.status;
        error.body = data;
        throw error;
    }

    return data;
};

const authorizedFetch = async (path, token, options = {}) => {
    if (!token) {
        throw new Error('Authentication required');
    }

    const headers = {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
    };

    const response = await fetch(buildUrl(path), {
        method: 'GET',
        ...options,
        headers,
    });

    return parseResponse(response);
};

export const getRatingUsage = (token) => authorizedFetch('/api/rating/usage', token);

export const getRatingHistory = (token) => authorizedFetch('/api/rating/history', token);

export const createRating = (symbol, token) =>
    authorizedFetch('/api/rating', token, {
        method: 'POST',
        body: JSON.stringify({ symbol }),
    });

export const getSnapshotAnalysisUsage = (token) =>
    authorizedFetch('/api/snapshot-analysis/usage', token);

export const createSnapshotAnalysis = ({ ticker, snapshotDate }, token) =>
    authorizedFetch('/api/snapshot-analysis', token, {
        method: 'POST',
        body: JSON.stringify({
            ticker: requiredParam(ticker, 'ticker').toUpperCase(),
            snapshot_date: requiredParam(snapshotDate, 'snapshotDate'),
        }),
    });

export const createStripeCheckout = (token, { successUrl, cancelUrl }) =>
    authorizedFetch('/api/stripe/checkout', token, {
        method: 'POST',
        body: JSON.stringify({
            success_url: successUrl,
            cancel_url: cancelUrl,
        }),
    });

export const listApiKeys = (token) => authorizedFetch('/api/api-keys', token);

export const createApiKey = (token, { name, idToken } = {}) =>
    authorizedFetch('/api/api-keys', token, {
        method: 'POST',
        body: JSON.stringify({ name: name || 'Default' }),
        headers: idToken ? { 'X-ID-Token': idToken } : {},
    });

export const revokeApiKey = (token, keyId) =>
    authorizedFetch(`/api/api-keys/${keyId}`, token, {
        method: 'DELETE',
    });

export const searchTickers = async (query) => {
    const trimmedQuery = String(query ?? '').trim();
    if (!trimmedQuery) {
        return { results: [] };
    }

    const response = await fetch(buildUrl(`/api/tickers/search?q=${encodeURIComponent(trimmedQuery)}`), {
        headers: {
            Accept: 'application/json',
        },
    });

    return parseResponse(response);
};

export const getStockClassifications = async ({ sector, signal } = {}) => {
    const params = new URLSearchParams();
    if (sector) params.set('sector', sector);
    const query = params.toString();
    const response = await fetch(
        buildUrl(`/api/tickers/classifications${query ? `?${query}` : ''}`),
        { headers: { Accept: 'application/json' }, signal },
    );
    return parseResponse(response);
};

export const browseStocks = async ({
    query,
    sector,
    industry,
    sort = 'name',
    limit = 24,
    offset = 0,
    signal,
} = {}) => {
    const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
        sort,
    });
    if (query) params.set('q', query);
    if (sector) params.set('sector', sector);
    if (industry) params.set('industry', industry);
    const response = await fetch(buildUrl(`/api/tickers/directory?${params.toString()}`), {
        headers: { Accept: 'application/json' },
        signal,
    });
    return parseResponse(response);
};

export const getStockPriceHistory = async ({ ticker, startDate, endDate, range, signal } = {}) => {
    const response = await fetch(
        buildUrl(buildStockPriceHistoryPath({ ticker, startDate, endDate, range })),
        {
            headers: {
                Accept: 'application/json',
            },
            signal,
        },
    );

    return parseResponse(response);
};

export const getStockPrice = async ({ ticker, signal } = {}) => {
    const params = new URLSearchParams({
        ticker: requiredParam(ticker, 'ticker').toUpperCase(),
    });
    const response = await fetch(buildUrl(`/api/stock-price?${params.toString()}`), {
        headers: { Accept: 'application/json' },
        signal,
    });
    return parseResponse(response);
};

export const getCustomMetrics = async (ticker, { signal } = {}) => {
    const response = await fetch(buildUrl(`/api/custom-metrics?ticker=${encodeURIComponent(ticker)}`), {
        headers: { Accept: 'application/json' },
        signal,
    });
    return parseResponse(response);
};

export const getPublicCustomMetrics = async (ticker) => {
    const query = ticker ? `?ticker=${encodeURIComponent(ticker)}` : '';
    const response = await fetch(buildUrl(`/api/public/custom-metrics${query}`), {
        headers: {
            Accept: 'application/json',
        },
    });

    return parseResponse(response);
};

export const getPublicStockCustomMetrics = async (ticker) => {
    const response = await fetch(buildUrl(`/api/public/stocks/${encodeURIComponent(ticker)}/metrics`), {
        headers: {
            Accept: 'application/json',
        },
    });

    return parseResponse(response);
};

export const getPublicStockCustomMetricDetail = async (ticker, metricSlug) => {
    const response = await fetch(
        buildUrl(
            `/api/public/stocks/${encodeURIComponent(ticker)}/metrics/${encodeURIComponent(metricSlug)}`,
        ),
        {
            headers: {
                Accept: 'application/json',
            },
        },
    );

    return parseResponse(response);
};

export const getStockBaskets = async (token) => {
    const headers = { Accept: 'application/json' };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(buildUrl('/api/stock-baskets'), {
        headers,
    });

    return parseResponse(response);
};

export const getStockBasketDetail = async (basketId, token) => {
    const headers = { Accept: 'application/json' };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(buildUrl(`/api/stock-baskets/${basketId}`), {
        headers,
    });

    return parseResponse(response);
};

export const createBasket = (token, data) =>
    authorizedFetch('/api/stock-baskets', token, {
        method: 'POST',
        body: JSON.stringify(data),
    });

export const getCustomMetricSuggestions = async (ticker, token) => {
    const query = ticker ? `?ticker=${encodeURIComponent(ticker)}` : '';
    return authorizedFetch(`/api/custom-metrics/suggestions${query}`, token);
};

export const getCustomMetricScanJob = (ticker, token) =>
    authorizedFetch(`/api/custom-metrics/scan-jobs?ticker=${encodeURIComponent(ticker)}`, token);

export const scanCustomMetricSuggestions = async ({ ticker, limit = 4 }, token) =>
    authorizedFetch('/api/custom-metrics/suggestions/scan', token, {
        method: 'POST',
        body: JSON.stringify({ ticker, limit }),
    });

export const reviewCustomMetricSuggestion = async (
    ticker,
    suggestionKey,
    { action, reviewedBy, rejectionReason, definition, previewValues } = {},
    token,
) =>
    authorizedFetch(
        `/api/custom-metrics/suggestions/${encodeURIComponent(ticker)}/${encodeURIComponent(suggestionKey)}/review`,
        token,
        {
            method: 'POST',
            body: JSON.stringify({
                action,
                reviewed_by: reviewedBy,
                rejection_reason: rejectionReason,
                definition,
                preview_values: previewValues,
            }),
        },
    );

export const getCustomMetricPreviewJobs = (ticker, token) =>
    authorizedFetch(`/api/custom-metrics/preview-jobs?ticker=${encodeURIComponent(ticker)}`, token);

export const previewCustomMetricSuggestion = async (
    ticker,
    suggestionKey,
    { definition, limit, force } = {},
    token,
) =>
    authorizedFetch(
        `/api/custom-metrics/suggestions/${encodeURIComponent(ticker)}/${encodeURIComponent(suggestionKey)}/preview`,
        token,
        {
            method: 'POST',
            body: JSON.stringify({
                definition,
                limit,
                force,
            }),
        },
    );

export const getEarningsReport = async ({ ticker, signal }) => {
    const params = new URLSearchParams({ ticker: requiredParam(ticker, 'ticker').toUpperCase() });
    return parseResponse(await fetch(buildUrl(`/api/earnings-report?${params}`), { signal }));
};
